import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import i18n from 'i18next';
import './charts.css';

const ModernPieChart = ({ data, title, namePrefix = '' }) => {
    // Track viewport width so legend can reposition below the pie on
    // narrow screens — the side legend cropped the pie on mobile.
    const [isNarrow, setIsNarrow] = useState(
        typeof window !== 'undefined' && window.innerWidth < 768
    );

    useEffect(() => {
        const onResize = () => setIsNarrow(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Transform data for Recharts
    const chartData = Object.keys(data).map((key, index) => ({
        name: i18n.t(`${namePrefix}${key}`),
        value: data[key],
        originalKey: key
    }));

    // Get colors from CSS variables or fallback to predefined colors
    const COLORS = [
        '#00897B', '#26C6DA', '#7DD3C0', '#00BFA5', '#80CBC4',
        '#4DB6AC', '#64B5F6', '#81C784', '#FFB74D', '#A1887F',
        '#4DD0E1', '#AED581', '#FFD54F', '#90CAF9', '#80DEEA'
    ];

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const total = chartData.reduce((sum, item) => sum + item.value, 0);
            const percentage = ((payload[0].value / total) * 100).toFixed(1);
            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip__title">
                        {payload[0].name}
                    </p>
                    <p className="chart-tooltip__row">
                        {i18n.t('n')}: {payload[0].value}
                    </p>
                    <p className="chart-tooltip__accent">
                        {percentage}%
                    </p>
                </div>
            );
        }
        return null;
    };

    const renderLegendText = (value) => {
        // Wrap long text - max 35 characters per line
        const maxLength = 35;
        if (value.length <= maxLength) {
            return <span className="chart-legend-text">{value}</span>;
        }

        // Split into multiple lines
        const words = value.split(' ');
        const lines = [];
        let currentLine = '';

        words.forEach(word => {
            if ((currentLine + ' ' + word).trim().length <= maxLength) {
                currentLine = (currentLine + ' ' + word).trim();
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        });
        if (currentLine) lines.push(currentLine);

        return (
            <span className="chart-legend-text chart-legend-text--multiline">
                {lines.map((line, i) => (
                    <span key={`${line}-${i}`}>
                        {line}
                        {i < lines.length - 1 && <br />}
                    </span>
                ))}
            </span>
        );
    };

    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (percent < 0.05) return null; // Don't show label if slice is too small

        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                className="chart-pie-label"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    // Same reasoning as ModernBarChart — accordion already provides
    // the glass surface, padding, and the title.
    return (
        <div style={{ width: '100%' }}>
            <ResponsiveContainer width="100%" height={isNarrow ? 420 : 500}>
                <PieChart>
                    <Pie
                        data={chartData}
                        cx={isNarrow ? '50%' : '40%'}
                        cy={isNarrow ? '40%' : '50%'}
                        labelLine={false}
                        label={renderCustomLabel}
                        outerRadius={isNarrow ? 90 : 110}
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                    >
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                className="chart-pie-cell"
                            />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        layout={isNarrow ? 'horizontal' : 'vertical'}
                        verticalAlign={isNarrow ? 'bottom' : 'middle'}
                        align={isNarrow ? 'center' : 'right'}
                        wrapperStyle={isNarrow ? {
                            fontSize: '13px',
                            lineHeight: '1.4',
                            paddingTop: '12px',
                        } : {
                            paddingLeft: '20px',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            maxWidth: '45%',
                            overflowY: 'auto',
                            maxHeight: '450px'
                        }}
                        iconType="circle"
                        iconSize={10}
                        formatter={renderLegendText}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ModernPieChart;
